/* Onboarding Wizard — hiërarchische multi-step flow (sectie 3).
 *
 * Steps: 1) aantal locaties 2) locatie-gegevens 3) groepen per locatie
 *         4) controle-overzicht + logo 5) klaar
 *
 * Deze container beheert alleen state + auto-save. Rendering per step
 * is opgesplitst in /pages/onboarding/wizard/Step[1-5]*.jsx.
 */
import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import WizardStepper from "./onboarding/wizard/WizardStepper";
import Step1Aantal from "./onboarding/wizard/Step1Aantal";
import Step2Locatie from "./onboarding/wizard/Step2Locatie";
import Step3Groepen from "./onboarding/wizard/Step3Groepen";
import Step4Controle from "./onboarding/wizard/Step4Controle";
import Step5Klaar from "./onboarding/wizard/Step5Klaar";

const initGroup = () => ({
  name: "", opvangsoort: "KDV", groepstype: "",
  leeftijd_min: 0, leeftijd_max: 4, capaciteit: 16, actief: true,
});

const initLocation = () => ({
  name: "", opvangtype: "KDV", lrk_number: "",
  adres: "", postcode: "", plaats: "", telefoon: "", email: "",
  capaciteit: 0, actief: true, aantal_groepen: 1, groepen: [initGroup()],
});

export default function OnboardingWizardPage() {
  const [step, setStep] = useState(1);
  const [aantal, setAantal] = useState(1);
  const [locaties, setLocaties] = useState([initLocation()]);
  const [currentLoc, setCurrentLoc] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Haal eigen organisatie (voor logo-upload endpoint + branding)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/organizations");
        const list = r.data?.items || (Array.isArray(r.data) ? r.data : [r.data]);
        const o = list?.[0];
        setOrgId(o?.id || "");
        setLogoUrl(o?.logo_url || "");
      } catch (e) { /* stil */ }
    })();
  }, []);

  // Laad opgeslagen voortgang bij mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/onboarding/wizard/progress");
        const s = r.data?.state;
        if (s && s.locaties?.length > 0) {
          setStep(s.step || 1);
          setAantal(s.aantal || 1);
          setLocaties(s.locaties);
          setCurrentLoc(s.currentLoc || 0);
          if (r.data?.updated_at) {
            setLastSaved(r.data.updated_at);
            toast.info("Vorige voortgang hervat");
          }
        }
      } catch (e) { /* geen voortgang */ }
      setProgressLoaded(true);
    })();
  }, []);

  // Auto-save bij state-verandering (na initial load), debounced
  useEffect(() => {
    if (!progressLoaded || step >= 5) return;
    const t = setTimeout(() => {
      api.post("/onboarding/wizard/progress",
        { state: { step, aantal, locaties, currentLoc } })
        .then(r => setLastSaved(r.data?.updated_at))
        .catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [step, aantal, locaties, currentLoc, progressLoaded]);

  // ---------- Handlers ----------
  const setAantalAndResize = (n) => {
    const nInt = Math.max(1, Math.min(30, parseInt(n) || 1));
    setAantal(nInt);
    setLocaties(prev => {
      if (prev.length === nInt) return prev;
      if (prev.length < nInt) {
        return [...prev, ...Array.from({ length: nInt - prev.length }, initLocation)];
      }
      return prev.slice(0, nInt);
    });
  };

  const updLoc = (idx, patch) => setLocaties(list =>
    list.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const setAantalGroepen = (idx, n) => {
    const nInt = Math.max(1, Math.min(20, parseInt(n) || 1));
    setLocaties(list => list.map((l, i) => {
      if (i !== idx) return l;
      let groepen = l.groepen || [];
      if (groepen.length < nInt) groepen = [...groepen, ...Array.from({ length: nInt - groepen.length }, initGroup)];
      else groepen = groepen.slice(0, nInt);
      return { ...l, aantal_groepen: nInt, groepen };
    }));
  };

  const updGroup = (locIdx, gIdx, patch) => setLocaties(list =>
    list.map((l, i) => i !== locIdx ? l : {
      ...l,
      groepen: l.groepen.map((g, j) => j === gIdx ? { ...g, ...patch } : g),
    }));

  const validateLocatie = (l) => {
    if (!l.name.trim()) return "Locatienaam ontbreekt";
    if (!l.lrk_number.trim()) return "LRK-nummer ontbreekt";
    const locCap = parseInt(l.capaciteit) || 0;
    const grpSum = (l.groepen || []).reduce((s, g) => s + (parseInt(g.capaciteit) || 0), 0);
    if (locCap > 0 && grpSum > locCap) {
      return `Som groepscapaciteiten (${grpSum}) overschrijdt locatie-capaciteit (${locCap})`;
    }
    return null;
  };

  const validateGroep = (g) => {
    if (!g.name.trim()) return "Groepsnaam ontbreekt";
    if (g.capaciteit <= 0) return "Groepscapaciteit moet >0";
    return null;
  };

  const handleLogoUpload = async (file) => {
    if (!file || !orgId) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo mag maximaal 5 MB zijn");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/organizations/${orgId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(r.data?.logo_url || "");
      toast.success("Logo opgeslagen in persistente storage");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Logo-upload mislukt");
    }
    setLogoUploading(false);
  };

  const submitAll = async () => {
    if (!orgId) { toast.error("Organisatie niet geladen"); return; }
    for (let i = 0; i < locaties.length; i++) {
      const lErr = validateLocatie(locaties[i]);
      if (lErr) { toast.error(`Locatie ${i + 1}: ${lErr}`); return; }
      for (let j = 0; j < locaties[i].groepen.length; j++) {
        const gErr = validateGroep(locaties[i].groepen[j]);
        if (gErr) { toast.error(`Locatie ${i + 1} — Groep ${j + 1}: ${gErr}`); return; }
      }
    }
    setSubmitting(true);
    let ok = 0;
    try {
      for (const loc of locaties) {
        const payload = {
          locatie: {
            name: loc.name, opvangtype: loc.opvangtype, lrk_number: loc.lrk_number,
            adres: loc.adres, postcode: loc.postcode, plaats: loc.plaats,
            telefoon: loc.telefoon, email: loc.email,
            capaciteit: parseInt(loc.capaciteit) || 0, actief: loc.actief,
          },
          groepen: loc.groepen.map(g => ({
            name: g.name, opvangsoort: g.opvangsoort, groepstype: g.groepstype,
            leeftijd_min: parseFloat(g.leeftijd_min) || 0,
            leeftijd_max: parseFloat(g.leeftijd_max) || 0,
            capaciteit: parseInt(g.capaciteit) || 0, actief: g.actief,
          })),
        };
        await api.post(`/organizations/${orgId}/onboarding/wizard/locatie-met-groepen`, payload);
        ok += 1;
      }
      toast.success(`${ok} locaties + ${locaties.reduce((s, l) => s + l.groepen.length, 0)} groepen aangemaakt`);
      try { await api.delete("/onboarding/wizard/progress"); } catch (e) { /* geen probleem */ }
      setStep(5);
    } catch (e) {
      toast.error(`${formatApiError(e.response?.data?.detail) || "Onboarding fout"} (${ok}/${locaties.length} gelukt)`);
    }
    setSubmitting(false);
  };

  const totalGroepen = locaties.reduce((s, l) => s + l.groepen.length, 0);
  const loc = locaties[currentLoc];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Onboarding wizard"
        subtitle="Richt uw organisatie stap voor stap in — locaties en groepen worden veilig gekoppeld."
        actions={lastSaved && step < 5 ? (
          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full"
                data-testid="wizard-autosave-indicator">
            ✓ Automatisch opgeslagen
          </span>
        ) : null}
      />

      <WizardStepper step={step} />

      {step === 1 && (
        <Step1Aantal
          aantal={aantal}
          onAantalChange={setAantalAndResize}
          onNext={() => setStep(2)}
        />
      )}

      {step === 2 && loc && (
        <Step2Locatie
          currentLoc={currentLoc}
          aantal={aantal}
          locatie={loc}
          onLocChange={(patch) => updLoc(currentLoc, patch)}
          onAantalGroepenChange={(n) => setAantalGroepen(currentLoc, n)}
          onBack={() => currentLoc === 0 ? setStep(1) : setCurrentLoc(c => c - 1)}
          onNextLoc={() => setCurrentLoc(c => c + 1)}
          onGotoGroepen={() => { setCurrentLoc(0); setStep(3); }}
        />
      )}

      {step === 3 && loc && (
        <Step3Groepen
          currentLoc={currentLoc}
          aantal={aantal}
          locatie={loc}
          onGroupChange={(gIdx, patch) => updGroup(currentLoc, gIdx, patch)}
          onBack={() => currentLoc === 0 ? setStep(2) : setCurrentLoc(c => c - 1)}
          onNextLoc={() => setCurrentLoc(c => c + 1)}
          onGotoReview={() => setStep(4)}
        />
      )}

      {step === 4 && (
        <Step4Controle
          locaties={locaties}
          aantal={aantal}
          totalGroepen={totalGroepen}
          logoUrl={logoUrl}
          logoUploading={logoUploading}
          onLogoUpload={handleLogoUpload}
          onBack={() => { setStep(3); setCurrentLoc(0); }}
          onSubmit={submitAll}
          submitting={submitting}
        />
      )}

      {step === 5 && (
        <Step5Klaar
          aantal={aantal}
          totalGroepen={totalGroepen}
          logoUrl={logoUrl}
        />
      )}
    </div>
  );
}
